import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Star, Trash2, Pencil, X, Upload } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CATS = [
  { slug: "landscapes", name: "Landscapes" },
  { slug: "buildings-and-barns", name: "Buildings & Barns" },
  { slug: "bins-and-equipment", name: "Bins & Farm Equipment" },
  { slug: "pickup-trucks", name: "Pickup Trucks" },
  { slug: "wildlife-and-animals", name: "Wildlife & Farm Animals" },
  { slug: "a-little-bit-vintage", name: "A Little Bit Vintage" },
];
const empty = { title: "", category: "landscapes", image: "", description: "", featured: false };

export default function Admin() {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [prints, setPrints] = useState([]);
  const [editing, setEditing] = useState(null); // print id or 'new'
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/prints", { headers: authHeaders }).then((r) => setPrints(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => { setForm({ title: p.title, category: p.category, image: p.image, description: p.description || "", featured: p.featured }); setEditing(p.id); };
  const close = () => { setEditing(null); setForm(empty); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `/api/media/${data.path}` }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title || !form.image) { toast.error("Add a title and a photo"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/prints", form, { headers: authHeaders });
      else await api.put(`/admin/prints/${editing}`, form, { headers: authHeaders });
      toast.success("Saved");
      close(); load();
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  const toggleFeatured = async (p) => {
    await api.put(`/admin/prints/${p.id}`, { featured: !p.featured }, { headers: authHeaders });
    load();
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/prints/${p.id}`, { headers: authHeaders });
    toast.success("Deleted"); load();
  };
  const restore = async () => {
    if (!window.confirm("Load your saved catalog backup? This replaces the current list of prints with your backed-up products.")) return;
    try {
      const { data } = await api.post("/admin/import-products", {}, { headers: authHeaders });
      toast.success(`Restored ${data.imported} prints from your backup`);
      load();
    } catch { toast.error("Could not restore backup"); }
  };

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-line bg-paper sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Prairie Prints MT" className="h-11 w-11 rounded-full object-cover mix-blend-multiply" />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-2xl tracking-tight">Prairie Prints — Studio</span>
              <span className="label text-stone -mt-0.5">{user?.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="view-site-btn" onClick={() => navigate("/")} className="label border border-line px-4 py-2.5 hover:border-ink transition-colors">View site</button>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/admin/login"); }} className="label flex items-center gap-2 bg-ink text-paper px-4 py-2.5 hover:bg-brick transition-colors"><LogOut size={15} /> Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <span className="label text-brick">Manage prints</span>
            <h1 className="font-serif text-5xl tracking-tight mt-2">{prints.length} prints in the gallery</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="restore-catalog-btn" onClick={restore} className="label border border-line px-5 py-3.5 hover:border-ink transition-colors">Restore saved catalog</button>
            <button data-testid="add-print-btn" onClick={openNew} className="label flex items-center gap-2 bg-ink text-paper px-6 py-3.5 hover:bg-brick transition-colors"><Plus size={16} /> Add a print</button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {prints.map((p) => (
            <div key={p.id} data-testid={`admin-print-${p.id}`} className="border border-line bg-paper">
              <div className="aspect-[4/3] overflow-hidden bg-paper-2">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-serif text-xl tracking-tight leading-tight">{p.title}</h3>
                    <span className="label text-stone">{p.category_name}</span>
                  </div>
                  <button data-testid={`feature-${p.id}`} onClick={() => toggleFeatured(p)} title="Featured"
                    className={`p-2 border transition-colors ${p.featured ? "bg-wheat border-wheat text-ink" : "border-line text-stone hover:border-ink"}`}>
                    <Star size={15} fill={p.featured ? "currentColor" : "none"} />
                  </button>
                </div>
                <div className="flex gap-2 mt-4">
                  <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="label flex-1 flex items-center justify-center gap-2 border border-line py-2.5 hover:border-ink transition-colors"><Pencil size={14} /> Edit</button>
                  <button data-testid={`delete-${p.id}`} onClick={() => del(p)} className="label flex items-center justify-center gap-2 border border-line py-2.5 px-4 text-brick hover:bg-brick hover:text-paper hover:border-brick transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
          <div data-testid="print-form" className="bg-paper border border-line w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-line">
              <h2 className="font-serif text-3xl tracking-tight">{editing === "new" ? "Add a print" : "Edit print"}</h2>
              <button data-testid="form-close" onClick={close} className="p-2 hover:text-brick"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="label text-stone">Photo</label>
                <div className="mt-2 border border-line aspect-[4/3] overflow-hidden bg-paper-2 flex items-center justify-center">
                  {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : <span className="label text-stone">No photo yet</span>}
                </div>
                <label className="label mt-3 inline-flex items-center gap-2 border border-line px-4 py-2.5 cursor-pointer hover:border-ink transition-colors">
                  <Upload size={14} /> {uploading ? "Uploading…" : "Upload photo"}
                  <input data-testid="upload-input" type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
              </div>
              <div>
                <label className="label text-stone">Title</label>
                <input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-line bg-paper px-4 py-3 mt-1 outline-none focus:border-ink transition-colors" />
              </div>
              <div>
                <label className="label text-stone">Category</label>
                <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full border border-line bg-paper px-4 py-3 mt-1 outline-none focus:border-ink transition-colors">
                  {CATS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label text-stone">Description</label>
                <textarea data-testid="form-description" value={form.description} rows={4}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Tell the story of this photo — where and when it was taken…"
                  className="w-full border border-line bg-paper px-4 py-3 mt-1 outline-none focus:border-ink transition-colors resize-none" />
                <p className="label text-stone mt-1">Leave blank to use the default studio story.</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-wheat" />
                <span className="label text-ink">Show on the homepage (featured)</span>
              </label>
              <button data-testid="form-save" onClick={save} disabled={saving || uploading}
                className="w-full label bg-ink text-paper py-4 hover:bg-brick transition-colors disabled:opacity-60">
                {saving ? "Saving…" : "Save print"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
